import { Button } from '@invent/wl-ui-kit-next/ui';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  CompactSearch,
  CondensedTip,
  DataTable,
  FilterBar,
  HelpPopover,
  NotPermitted,
  SelectChip,
  useCondensed,
  STICKY_TABLE_TOP,
  type DataTableState,
  type IDataColumn
} from '@/design';
import { useCan } from '@/domains/auth';
import type {
  FirmRoute,
  FirmRouteFilter,
  FirmRouteSort,
  IAdminTeamFirm,
  IFirmRouteRow,
  IFirmRoutes
} from '../api/team-firm-contracts';
import { ROUTES_MAX_LIMIT, teamFirmsApi } from '../api/team-firms-api';
import { LoadError, PastTheEnd, useAdminData } from '../components/admin-page';
import { FIRM_LIST_REACH } from '../firm-lists';
import { countOf } from '../lists';
import { TEAM_PATHS } from '../paths';
import { SortHeader } from './compliance/queue-headers';
import { NoteLine } from './person-hub/said';
import { useReadAt } from './lists/work-page/header-actions';
import { WorkPager, dropPaging, readPages, useWorkPaging } from './lists/work-page/pager';
import { AddFirmDialog, NameChip, changeStamp, crdOf, useWriteNote } from './team-firm-form';
import { FirmRecordDialog, ROUTE_LABELS, SwitchDialog, WiresSwitch } from './team-firm-record';
import { NOT_MATCHED_ANCHOR, UnmatchedFirms } from './team-firms-unmatched';
import { TeamRoutingHeader, useControlsEnd } from './team-routing-frame';

/**
 * Team routing (SR-513, `/team/setup/team-routing`): **Firms**, one table of every firm a team can
 * be routed by — a legal entity by CRD, whether it is on the wirehouse list (Wires), named by a
 * current BrokerCheck or IAPD employment, or only named on the team firm register — over
 * survey-api's `GET /admin/team-firms/routes` under `responses.browse`. survey-api searches (name,
 * other names and their spellings, CRD), filters, orders and pages them, and counts both routes
 * under the search; this screen draws the window asked. A row's other names are the register
 * names mapped to its CRD.
 *
 * **A light work page** (the work pages design, "Three forms"): the header with *Refresh* and the
 * "?", the submitted search, the Route quick filter — All, Wires, Gating, each with the count the
 * rows' read brings — a column's label sorting by it (teams seen, most first, unless asked), the
 * table under a sticky header, the work pages' pager, and the record over the list. Above it,
 * "Not matched yet", drawn only when it holds something (`team-firms-unmatched.tsx`).
 *
 * **The list is the URL** (`q`, `route`, `sort`, `dir`, `page`, `size`), and so is the open record
 * (`firm`, a CRD): a row's press pushes it, so Back closes the record, and its own close goes back
 * where the press pushed. A window is drawn only under the query it answers: the last stays while
 * the next is out, and goes when the next one's read fails. A re-read after a write that fails
 * over a window already drawn keeps it, as last read, and says so.
 *
 * **Writes**, all `responses.annotate`, drawn off under one `NotPermitted` without it: the Wires
 * switch, on a row and in the record, behind a confirmation that says what it does to a team;
 * "Add a firm" (`AddFirmDialog`); and the record's rename and other names. None moves a team at
 * once. A success reads the window and the register again and says what happened above the table.
 */
export function AdminTeamFirmRoutesPage() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const q = params.get('q') ?? '';
  const route = filterOf(params.get('route'));
  const sort = sortOf(params.get('sort'));
  const dir: 'asc' | 'desc' = params.get('dir') === 'asc' ? 'asc' : 'desc';
  const opened = crdOf(params.get('firm') ?? '');
  const [draft, setDraft] = React.useState(q);
  React.useEffect(() => setDraft(q), [q]);
  const paging = useWorkPaging();
  const { page, perPage, pages } = paging;

  const asked = JSON.stringify([route, q, sort, dir, page, perPage, pages]);
  const { data, error, reload, reading } = useAdminData<IAnswered>(
    async (signal) => ({
      asked,
      q,
      route,
      page,
      perPage,
      read: await readRoutes({ route, q, sort, dir }, page, perPage, pages, signal)
    }),
    [asked]
  );
  const registerRead = useAdminData<IAdminTeamFirm[]>((signal) => teamFirmsApi.list(signal), []);
  const register = registerRead.data;
  // The last window while the next is out; nothing once this query's read has failed — but a
  // re-read of the same query that failed keeps the window, as last read. An error stands only
  // while no newer read is out: an old one is no answer to the query just asked.
  const failed = error !== null && !reading;
  const shown = failed && data?.asked !== asked ? null : data;
  // Why Add a firm and the record's "Add another name" are off: they pick from the register.
  const registerMissing =
    registerRead.data !== null
      ? undefined
      : registerRead.error !== null
        ? 'Needs the register, which could not be read.'
        : 'Reading the register…';
  const readAt = useReadAt(data);

  const permitted = useCan()('responses.annotate');
  const reasonId = React.useId();
  const notPermitted = permitted ? undefined : reasonId;
  const [switching, setSwitching] = React.useState<Nullable<IFirmRouteRow>>(null);
  const [adding, setAdding] = React.useState<Nullable<{ crd: string; name: string }>>(null);
  /** The table and the register, read again — what every write changes. */
  const rereadAll = React.useCallback(async () => {
    const [rows, names] = await Promise.all([reload(), registerRead.reload()]);
    return rows && names;
  }, [reload, registerRead.reload]);
  const { note, setNote, landed, lost } = useWriteNote(rereadAll, 'firm');
  // What a write said is about the window it was made on.
  React.useEffect(() => setNote(null), [page, perPage, pages]);

  /** A change of search, filter or order starts at the top. */
  const update = (mutate: (next: URLSearchParams) => void, replace = false) => {
    setNote(null);
    setParams(
      (now) => {
        const next = dropPaging(new URLSearchParams(now));
        mutate(next);
        return next;
      },
      { replace }
    );
  };
  const search = (text: string) =>
    update((next) => (text === '' ? next.delete('q') : next.set('q', text)), true);
  const filter = (value: Nullable<string>) =>
    update((next) => (value === null ? next.delete('route') : next.set('route', value)));
  // A press on the order in force turns it round; on another column, sorts by it its first way.
  const sortBy = (column: FirmRouteSort) =>
    update((next) => {
      const nextDir = sort === column ? (dir === 'asc' ? 'desc' : 'asc') : FIRST_DIR[column];
      if (column === DEFAULT_SORT && nextDir === 'desc') {
        next.delete('sort');
        next.delete('dir');
      } else {
        next.set('sort', column);
        next.set('dir', nextDir);
      }
    });

  /** A firm's record, opened — a row's, or the one Add a firm found the CRD is: pushed, so Back
   *  closes it. */
  const openFirm = (crd: number) => {
    setNote(null);
    const next = new URLSearchParams(params);
    next.set('firm', String(crd));
    setParams(next, { state: { firmPushed: true } });
  };
  /** The record closed: back where its press pushed from, or — a pasted address — without it. */
  const closeFirm = () => {
    if ((location.state as Nullable<{ firmPushed?: boolean }>)?.firmPushed) navigate(-1);
    else {
      const next = new URLSearchParams(params);
      next.delete('firm');
      setParams(next, { replace: true });
    }
  };

  const columns = React.useMemo(
    () =>
      firmColumns({ sort, dir, onSort: sortBy }, notPermitted, (firm) => {
        setNote(null);
        setSwitching(firm);
      }),
    // `sortBy` reads the newest address, which a new query brings.
    [sort, dir, notPermitted, params]
  );

  const counts = shown?.q === q ? shown.read.counts : undefined;
  const state: DataTableState | undefined =
    shown === null
      ? failed
        ? { kind: 'error', content: <LoadError message={error} /> }
        : { kind: 'loading' }
      : shown.read.items.length > 0
        ? undefined
        : {
            kind: 'empty',
            content:
              shown.read.total > 0 ? (
                <PastTheEnd
                  page={shown.page}
                  pageSize={shown.perPage}
                  total={shown.read.total}
                  counted={['firm', 'firms']}
                  onPage={paging.setPage}
                />
              ) : (
                emptyText(shown)
              )
          };

  const { end, scrolled } = useControlsEnd();
  const refresh = {
    reading: reading || registerRead.reading,
    readAt,
    onClick: () => void rereadAll()
  };
  const help = <AboutFirms />;
  const searchBox = {
    label: 'Search firms',
    placeholder: 'Firm, other name or CRD',
    draft,
    onDraft: setDraft,
    onSubmit: () => search(draft)
  };
  const routeChip = (
    <SelectChip
      label="Route"
      any={counts ? `All ${(counts.wires + counts.gating).toLocaleString('en-US')}` : 'All'}
      value={route === 'all' ? null : route}
      options={ROUTES.map((value) => ({
        value,
        label: counts ? `${ROUTE_LABELS[value]} ${counts[value].toLocaleString('en-US')}` : ROUTE_LABELS[value]
      }))}
      onChoose={filter}
    />
  );
  const missingId = React.useId();
  const addFirm = (
    <AddFirmButton
      disabled={!permitted || register === null}
      describedBy={
        [notPermitted, registerMissing ? missingId : undefined].filter(Boolean).join(' ') ||
        undefined
      }
      onClick={() => {
        setNote(null);
        setAdding({ crd: '', name: '' });
      }}
    />
  );
  const addButton = (
    <span className="flex items-center gap-2">
      {registerMissing && (
        <span id={missingId} className="text-[0.8125rem] text-muted-foreground">
          {registerMissing}
        </span>
      )}
      {addFirm}
    </span>
  );

  return (
    <>
      <TeamRoutingHeader
        refresh={refresh}
        help={help}
        scrolled={scrolled}
        compact={
          <>
            <CompactSearch {...searchBox} />
            {routeChip}
            {addFirm}
          </>
        }
      />
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {register !== null && (
          <UnmatchedFirms
            register={register}
            reread={rereadAll}
            notPermitted={notPermitted}
            onAddAsFirm={(seen) => {
              setNote(null);
              setAdding({ crd: String(seen.finraFirmId), name: seen.firmName ?? '' });
            }}
          />
        )}
        <FilterBar
          search={searchBox}
          onClear={
            q !== '' || route !== 'all'
              ? () =>
                  update((next) => {
                    next.delete('q');
                    next.delete('route');
                  })
              : undefined
          }
          busy={reading}
          count={shown ? countOf(shown.read.total, 'firm') : undefined}
          end={addButton}
        >
          {routeChip}
        </FilterBar>
        {end}
        {!permitted && <NotPermitted id={reasonId} what="changing firms" />}

        {failed && shown !== null && (
          <LoadError message={`Could not refresh — showing the firms as last read. ${error}`} />
        )}
        {registerRead.error && (
          <LoadError message={`Could not read the register. ${registerRead.error}`} />
        )}
        {note && <NoteLine note={note} />}

        <DataTable
          label="Firms"
          columns={columns}
          rows={state ? null : (shown?.read.items ?? null)}
          rowKey={(firm) => String(firm.finraFirmId)}
          rowProps={(firm) =>
            ({
              'data-finra-firm': firm.finraFirmId,
              'data-route': firm.route
            }) as React.HTMLAttributes<HTMLTableRowElement>
          }
          onOpen={(firm) => openFirm(firm.finraFirmId)}
          selected={opened === null ? null : String(opened)}
          state={state}
          busy={reading && shown !== null}
          minWidth="64rem"
          stickyTop={STICKY_TABLE_TOP}
        />

        {shown && <WorkPager {...paging.pager} total={shown.read.total} busy={reading} sticky />}
      </div>

      {opened !== null && (
        <FirmRecordDialog
          key={opened}
          crd={opened}
          initial={shown?.read.items.find((firm) => firm.finraFirmId === opened)}
          register={register}
          registerMissing={registerMissing}
          permitted={permitted}
          onClose={closeFirm}
          onChanged={rereadAll}
        />
      )}
      {switching && (
        <SwitchDialog
          firm={switching}
          onClose={() => setSwitching(null)}
          landed={landed}
          lost={lost}
        />
      )}
      {adding && register !== null && (
        <AddFirmDialog
          register={register}
          initialCrd={adding.crd}
          initialName={adding.name}
          onClose={() => setAdding(null)}
          onOpenFirm={(crd) => {
            setAdding(null);
            openFirm(crd);
          }}
          onLanded={(said, closed) => {
            if (closed) setAdding(null);
            void landed(said);
          }}
          onLost={() => {
            setAdding(null);
            void lost();
          }}
        />
      )}
    </>
  );
}

/** The "?": what the table holds, what the switch does, the other names and the search. */
function AboutFirms() {
  return (
    <HelpPopover label="About firms" align="end">
      <p>
        Every firm a team can be routed by, one row per CRD. A firm with Wires on is on the
        wirehouse list: a team whose lead works there takes the Wires route, and the survey’s
        wirehouse questions apply. Every other firm is Gating: a team there goes to the first gating
        question.
      </p>
      <p>
        The name is the list’s label on Wires, else the BrokerCheck or IAPD spelling, else its first
        other name. Other names are the register’s names for the CRD: a registration there is named
        by them, and Rankings group the firm’s advisors under them. Teams and People count the
        participants whose latest lookup names the firm as a current employer. The search matches a
        name, an other name or its spellings, or a CRD.
      </p>
      <p>
        Open a firm for its names and the teams and people seen there. Turning Wires on or off,
        adding a firm to Wires or renaming a Wires firm edits the wirehouse list. {FIRM_LIST_REACH}
      </p>
    </HelpPopover>
  );
}

/** Pages `page` to `page + pages - 1` of `perPage` firms as one answer — survey-api's offset and
 *  limit, read once where the window starts the list and fits its ceiling, else a page at a time. */
async function readRoutes(
  query: { route: FirmRouteFilter; q: string; sort: FirmRouteSort; dir: 'asc' | 'desc' },
  page: number,
  perPage: number,
  pages: number,
  signal: AbortSignal
): Promise<IFirmRoutes> {
  const parts = await readPages(
    (at, size) => teamFirmsApi.routes({ ...query, offset: (at - 1) * size, limit: size }, signal),
    page,
    perPage,
    pages,
    ROUTES_MAX_LIMIT
  );
  return { ...parts[0], items: parts.flatMap((part) => part.items) };
}

/**
 * Team routing's addresses before it was one table, and the area's older ones through them: a
 * route's (`/wires`, `/gating`) the table under that filter, the register's the
 * table, and the firms seen at registration the table at "Not matched yet" — the search carried.
 */
export function AdminTeamFirmsRouteRedirect({
  route,
  notMatched = false
}: {
  route?: FirmRoute;
  notMatched?: boolean;
}) {
  const { search } = useLocation();
  const next = new URLSearchParams(search);
  if (route) next.set('route', route);
  const query = next.toString();
  return (
    <Navigate
      to={{
        pathname: TEAM_PATHS.teamRouting,
        search: query === '' ? '' : `?${query}`,
        hash: notMatched ? NOT_MATCHED_ANCHOR : ''
      }}
      replace
    />
  );
}

const ROUTES: readonly FirmRoute[] = ['wires', 'gating'];

/** The address's `route`, any other spelling read as All. */
function filterOf(value: Nullable<string>): FirmRouteFilter {
  return value === 'wires' || value === 'gating' ? value : 'all';
}

const SORTS: readonly FirmRouteSort[] = ['teams', 'people', 'name', 'crd', 'changed'];
const DEFAULT_SORT: FirmRouteSort = 'teams';

/** The address's `sort`, any other spelling read as the default. */
function sortOf(value: Nullable<string>): FirmRouteSort {
  return SORTS.find((sort) => sort === value) ?? DEFAULT_SORT;
}

/** The way a column first sorts: counts and the last change from the most, names and CRDs up. */
const FIRST_DIR: Record<FirmRouteSort, 'asc' | 'desc'> = {
  teams: 'desc',
  people: 'desc',
  name: 'asc',
  crd: 'asc',
  changed: 'desc'
};

/** A window of the list, with the query it answers. */
interface IAnswered {
  asked: string;
  q: string;
  route: FirmRouteFilter;
  page: number;
  perPage: number;
  read: IFirmRoutes;
}

/** What a window with no firm says — of the query it answers, not the one being asked. */
function emptyText({ q, route }: IAnswered): string {
  if (q === '') {
    return route === 'all'
      ? 'No firm is on the wirehouse list, seen or named yet.'
      : `No firm is on ${ROUTE_LABELS[route]}.`;
  }
  return route === 'all'
    ? 'No firm matches this search.'
    : `No firm on ${ROUTE_LABELS[route]} matches this search.`;
}

/**
 * "Add a firm", in the filter bar and — slimmed to its icon, named the same — in the compact bar,
 * which draws the page's search, filter and action once they scroll away.
 */
function AddFirmButton({
  disabled,
  describedBy,
  onClick
}: {
  disabled: boolean;
  describedBy?: string;
  onClick: () => void;
}) {
  const { condensed } = useCondensed();
  return (
    <CondensedTip label="Add a firm">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={condensed ? 'size-8 p-0' : undefined}
        aria-label={condensed ? 'Add a firm' : undefined}
        disabled={disabled}
        aria-describedby={describedBy}
        onClick={onClick}
      >
        <Plus className="size-4" aria-hidden />
        {!condensed && 'Add a firm'}
      </Button>
    </CondensedTip>
  );
}

/** The order in force, and the press that changes it. */
interface ISorting {
  sort: FirmRouteSort;
  dir: 'asc' | 'desc';
  onSort: (column: FirmRouteSort) => void;
}

/** How many other names a row draws before it says how many more. */
const OTHER_NAMES_SHOWN = 3;

/** A row's other names: its register names, but one spelled as the name shown. */
function otherNames(firm: IFirmRouteRow): string[] {
  const shown = firm.name?.trim().toLowerCase();
  return firm.registerFirms
    .map((name) => name.label)
    .filter((label) => label.trim().toLowerCase() !== shown);
}

/** The table's columns: the firm, its CRD, its other names, its teams and people, the Wires
 *  switch, and the list member's last change. Every label but the names' and the switch's sorts. */
function firmColumns(
  sorting: ISorting,
  notPermitted: string | undefined,
  flip: (firm: IFirmRouteRow) => void
): IDataColumn<IFirmRouteRow>[] {
  const head = (column: FirmRouteSort, label: string) => {
    const active = sorting.sort === column;
    const direction = active ? sorting.dir : FIRST_DIR[column];
    return (
      <SortHeader
        label={label}
        direction={direction}
        active={active}
        note={`${active ? 'Sorted' : 'Sort'} by ${label.toLowerCase()}, ${direction === 'asc' ? 'ascending' : 'descending'}`}
        onPress={() => sorting.onSort(column)}
      />
    );
  };
  return [
    {
      id: 'firm',
      header: head('name', 'Firm'),
      label: 'Firm',
      cell: (firm) =>
        firm.name === null ? (
          <span className="text-muted-foreground">No name yet</span>
        ) : (
          <span className="font-medium break-words">{firm.name}</span>
        )
    },
    {
      id: 'crd',
      header: head('crd', 'CRD'),
      label: 'CRD',
      className: 'font-mono text-xs tabular-nums',
      cell: (firm) => firm.finraFirmId
    },
    {
      id: 'other-names',
      header: 'Other names',
      cell: (firm) => {
        const names = otherNames(firm);
        if (names.length === 0) return null;
        return (
          <span className="flex flex-wrap gap-1">
            {names.slice(0, OTHER_NAMES_SHOWN).map((label) => (
              <NameChip key={label} text={label} />
            ))}
            {names.length > OTHER_NAMES_SHOWN && (
              <span className="self-center text-xs text-muted-foreground">
                +{names.length - OTHER_NAMES_SHOWN} more
              </span>
            )}
          </span>
        );
      }
    },
    {
      id: 'teams',
      header: head('teams', 'Teams'),
      label: 'Teams',
      numeric: true,
      cell: (firm) => firm.teams.toLocaleString('en-US')
    },
    {
      id: 'people',
      header: head('people', 'People'),
      label: 'People',
      numeric: true,
      cell: (firm) => firm.people.toLocaleString('en-US')
    },
    {
      id: 'wires',
      header: 'Wires',
      cell: (firm) => (
        <WiresSwitch firm={firm} notPermitted={notPermitted} onFlip={() => flip(firm)} />
      )
    },
    {
      id: 'last-change',
      header: head('changed', 'Last change'),
      label: 'Last change',
      className: 'text-xs text-muted-foreground',
      headClassName: 'min-w-44',
      cell: (firm) =>
        firm.route === 'gating' || firm.updatedAt === null
          ? '—'
          : changeStamp(firm.updatedAt, firm.updatedBy)
    }
  ];
}
